<template>
  <div class="mdb-vector-map" ref="wrapper" :style="{ backgroundColor }">
    <toolbar
      v-if="interactive"
      :fill="fill"
      :stroke="stroke"
      @zoomIn="zoom(2)"
      @zoomOut="zoom(-2)"
    />

    <div ref="map" class="mdb-vector-map__wrapper">
      <svg
        :style="svgStyle"
        :viewBox="svgViewBox"
        :height="height"
        :width="width"
        preserveAspectRatio="xMinYMin"
        @mousedown="handleDragStart"
        @mousemove="handleDrag"
        @touchstart.prevent="handleDragStart"
        @touchmove.prevent="handleDrag"
        @touchend.prevent="handleDragEnd"
      >
        <path
          v-for="(region, i) in data"
          :key="i"
          :ref="region.id"
          :fill="
            selected === region.id
              ? selectFill
              : hoverable && hover === region.id
              ? hoverFill
              : options && options[region.id] && options[region.id].fill
              ? options[region.id].fill
              : fill
          "
          :stroke="stroke"
          :d="region.path"
          :opacity="opacity"
          :stroke-opacity="strokeOpacity"
          :stroke-width="strokeWidth"
          :stroke-linejoin="strokeLinejoin"
          :style="{ cursor: hoverable && !dragged ? 'pointer' : 'inherit' }"
          transform="matrix(0.3474,0,0,0.3474,0,0)"
          :fill-opacity="fillOpacity"
          @click="selectRegion(region.id)"
          @mouseover="handleMouseover($event, region.id)"
          @mouseleave="handleMouseleave"
          @mousemove="setTooltip"
          @touchstart="selectRegion(region.id)"
        ></path>
      </svg>
    </div>

    <div
      v-show="hover && tooltip"
      class="mdb-vector-map__tooltip"
      :style="tooltipStyle"
      v-html="tooltipText"
    ></div>

    <Legend v-if="legend" :legend="legend" />
  </div>
</template>

<script>
import Toolbar from "./Toolbar";
import Legend from "./Legend";

export default {
  props: {
    map: {
      type: [String, Array],
      default: "world"
    },
    selectable: {
      type: Boolean,
      default: true
    },
    width: String,
    height: String,
    options: Object,
    preselected: String,
    hoverable: {
      type: Boolean,
      default: true
    },
    tooltip: {
      type: Boolean,
      default: true
    },
    fill: {
      type: String,
      default: "#90caf9"
    },
    selectFill: {
      type: String,
      default: "#2196f3"
    },
    hoverFill: {
      type: String,
      default: "#2196f3"
    },
    stroke: {
      type: String,
      default: "#fff"
    },
    opacity: {
      type: Number,
      default: 1
    },
    strokeOpacity: {
      type: Number,
      default: 1
    },
    fillOpacity: {
      type: Number,
      default: 1
    },
    strokeWidth: {
      type: Number,
      default: 1.5
    },
    strokeLinejoin: {
      type: String,
      default: "round"
    },
    backgroundColor: {
      type: String,
      default: "transparent"
    },
    interactive: {
      type: Boolean,
      default: true
    },
    legend: Object,
    viewBox: String
  },
  components: {
    Toolbar,
    Legend
  },
  data() {
    return {
      data: [],
      scale: 1,
      dragged: false,
      dx: 0,
      dy: 0,
      selected: false,
      hover: false,
      tooltipY: 0,
      tooltipX: 0,
      origin: { x: 0, y: 0 },
      svgViewBox: "0 0 700 300",
      distance: 0,
    };
  },
  computed: {
    svgStyle() {
      return {
        cursor: this.dragged ? "move" : "default",
        transformOrigin: `${this.origin.x}px ${this.origin.y}px`,
        transform: `matrix(${this.scale}, 0, 0, ${this.scale}, ${this.dx}, ${this.dy})`
      };
    },
    tooltipText() {
      let defaultText = this.data.find(region => region.id === this.hover)
        ? this.data.find(region => region.id === this.hover).name
        : "";

      return this.options &&
        this.options[this.hover] &&
        this.options[this.hover].tooltip
        ? this.options[this.hover].tooltip
        : defaultText;
    },
    tooltipStyle() {
      return {
        backgroundColor: this.stroke,
        color: this.fill,
        borderColor: this.fill,
        top: `${this.tooltipY}px`,
        left: `${this.tooltipX}px`
      };
    },
    transform() {
      return `matrix(${this.scale} 0 0 ${this.scale} ${this.dx} ${this.dy})`;
    },
    center() {
      return {
        x: this.$refs.map.getBoundingClientRect().width / 2,
        y: this.$refs.map.getBoundingClientRect().y / 2
      };
    }
  },
  methods: {
    selectRegion(code) {
      if (this.selectable) {
        this.selected = code;
        this.$emit("select", code);
      }
    },
    handleMouseover(e, id) {
      this.hover = id;
    },
    setTooltip(e) {
      this.tooltipY = e.clientY - this.$refs.map.getBoundingClientRect().y + 10;
      this.tooltipX = e.clientX - this.$refs.map.getBoundingClientRect().x + 10;
    },
    handleMouseleave() {
      this.hover = false;
    },
    calculateOrigin(point) {
      const position = {
        x: point.x - this.$refs.map.getBoundingClientRect().x,
        y: point.y - this.$refs.map.getBoundingClientRect().y
      };

      const dx = (position.x - this.origin.x) / this.scale;
      const dy = (position.y - this.origin.y) / this.scale;

      return {
        x: this.origin.x + dx,
        y: this.origin.y + dy
      };
    },
    handleScroll(e) {
      e.preventDefault();
      const point = { x: e.x, y: e.y };

      const origin = this.calculateOrigin(point);

      let delta = e.deltaY > 0 ? 0.5 : 1.5;
      this.zoom(delta, origin);
    },

    zoom(delta, origin = false) {
      const scale = this.scale * delta;

      if (origin && delta > 1) {
        this.origin = {...origin};

      } else if (!origin) {
        this.origin = { ...this.center };
      }

      if (scale > 1) {
        this.scale = scale;
      } else {
        this.scale = 1;
        this.dx = 0;
        this.dy = 0;
      }
    },
    calculateDistance(touches) {
      const [touch1, touch2] = touches;
      const x = touch2.clientX - touch1.clientX;
      const y = touch2.clientY - touch1.clientY;

      return Math.sqrt(x * x + y * y);
    },
    handleDragStart(e) {
      let position;

      if (e.type === "touchstart") {
        if (e.touches.length === 2) {
          this.distance = this.calculateDistance(e.touches);
          return;
        }

        let [touch] = e.touches;
        position = touch;
      } else {
        position = e;
      }

      this.dragged = true;
      this.position = {
        x: position.clientX,
        y: position.clientY
      };
    },
    handleDrag(e) {
      let position;
      if (e.type === "touchmove") {
        if (e.touches.length === 2) {
          let distance = this.calculateDistance(e.touches);
          let scale = distance / this.distance;
          let center = {
            x:
              e.touches[0].clientX +
              (e.touches[1].clientX - e.touches[0].clientX) / 2,
            y:
              e.touches[0].clientY +
              (e.touches[1].clientY - e.touches[0].clientY) / 2
          };

          let origin = this.calculateOrigin(center);

          this.distance = distance;

          this.zoom(scale, origin);

          return;
        }

        let [touch] = e.touches;
        position = touch;
      } else {
        position = e;
      }

      if (this.dragged && this.interactive) {
        this.dx += position.clientX - this.position.x;
        this.dy += position.clientY - this.position.y;

        this.position = {
          x: position.clientX,
          y: position.clientY
        };
      }
    },
    handleDragEnd() {
      this.dragged = false;
    },
    setMap() {
      this.dx = 0;
      this.dy = 0;
      this.scale = 1;

      if (typeof this.map === "string") {
        let mapName = this.map.length > 2 ? this.map : this.map.toLowerCase();

        const map = require(`./maps/${mapName}`).default;

        this.data = map.map;
        this.svgViewBox = this.viewBox || map.viewbox || "0 0 700 300";
      } else {
        this.data = this.map;
        this.svgViewBox = this.viewBox || "0 0 700 300";
      }
    }
  },
  mounted() {
    this.setMap();

    if (this.interactive)
      this.$refs.map.addEventListener("wheel", this.handleScroll);

    if (this.interactive) {
      window.addEventListener("mouseup", this.handleDragEnd);
    }

    if (this.preselected) this.selected = this.preselected;

    this.origin = { ...this.center };
  },
  beforeDestroy() {
    if (this.interactive)
      window.removeEventListener("mouseup", this.handleDragEnd);
  },
  watch: {
    preselected(value) {
      this.selected = value;
    },
    map() {
      this.setMap();
    }
  }
};
</script>

<style scoped>
.mdb-vector-map {
  max-width: 100%;
  max-height: 100%;
  position: relative;
  overflow: hidden;
}

.mdb-vector-map path {
  transition: fill 0.3s linear;
}

.mdb-vector-map__tooltip {
  position: absolute;
  z-index: 9999999;
  border-style: solid;
  border-width: 1px;
  padding: 10px;
  border-radius: 5px;
  font-size: 12px;
  pointer-events: none;
  text-align: center;
  line-height: 1.5;
  top: 0;
  left: 0;
}
</style>
